import React, { useState, useEffect, useRef, useMemo } from "react";
import { connect } from "react-redux";
import DataGrid from "devextreme-react/data-grid";
import { GetTransactionHeaderGridData } from "../services/ItemManagementService";
import { TRANSACTION_TYPES, STAGES, STATUSES } from "./codeLists";
import TransactionsFilters from "./TransactionsFilters";
import DetailTabs from "./DetailTabs";

const RANGE10 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const money = { type: "fixedPoint", precision: 2 };
const lookup = (list) => ({ dataSource: list, valueExpr: "code", displayExpr: "label" });

// The Delphi grdOpenTransactionsTop columns (frmIMM0046) are shown; the rest of the query's fields
// (totals, fiscal, and the 4x10 user-defined passthroughs) are added hidden for the column chooser.
// ROWID is the row key, not a column. The Historical grid omits the Stage column.
const buildHeaderColumns = (historical) => {
  const cols = [
    { dataField: "recordNumber", caption: "Record #" },
    { dataField: "transactionType", caption: "Trans Type", lookup: lookup(TRANSACTION_TYPES) },
    { dataField: "stage", caption: "Stage", lookup: lookup(STAGES) },
    { dataField: "transactionDate", caption: "Trans Date", dataType: "datetime", format: "MM/dd/yyyy hh:mm a" },
    { dataField: "reference", caption: "Reference #" },
    { dataField: "status", caption: "Status", lookup: lookup(STATUSES) },
    { dataField: "description", caption: "Description" },
    { dataField: "totalAmount", caption: "Total Amount", dataType: "number", format: money },
    { dataField: "totalQuantity", caption: "Total Quantity", dataType: "number", format: money, visible: false },
    { dataField: "fiscalYear", caption: "Fiscal Year", dataType: "number", visible: false },
    { dataField: "fiscalPeriod", caption: "Fiscal Period", dataType: "number", visible: false },
    ...RANGE10.map((i) => ({ dataField: `code${i}`, caption: `Code - ${i}`, visible: false })),
    ...RANGE10.map((i) => ({ dataField: `reference${i}`, caption: `Reference - ${i}`, visible: false })),
    ...RANGE10.map((i) => ({ dataField: `date${i}`, caption: `Date - ${i}`, dataType: "date", format: "MM/dd/yyyy", visible: false })),
    ...RANGE10.map((i) => ({ dataField: `quantity${i}`, caption: `Quantity - ${i}`, dataType: "number", format: money, visible: false })),
  ];
  return historical ? cols.filter((c) => c.dataField !== "stage") : cols;
};

// Master (transactions) + detail (lines). Selecting a top row pushes it to DetailTabs via a ref, which
// re-fetches that header's lines — the SalesOrderManagement master-detail pattern.
function Transactions(props) {
  const { historical } = props;
  const accessToken = props.oidc?.user?.access_token;
  const currentUser = props.oidc?.user;
  const dynamicFormSetting = props.oidc?.user?.profile?.DYNAMICFORMSETTING;

  const [dataSource, setDataSource] = useState(null);
  const detailRef = useRef(null);

  const searchData = (formFields) => {
    setDataSource(GetTransactionHeaderGridData(accessToken, historical, formFields));
  };

  // Load once on mount / when the tab (open vs historical) changes.
  useEffect(() => {
    setDataSource(GetTransactionHeaderGridData(accessToken, historical, {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historical, accessToken]);

  const onSelectionChanged = ({ selectedRowsData }) => {
    const row = selectedRowsData && selectedRowsData.length ? selectedRowsData[0] : null;
    if (detailRef.current) {
      detailRef.current.updateData(row);
    }
  };

  const pager = useMemo(() => ({ visible: true, showPageSizeSelector: true, allowedPageSizes: [20, 50, 100], showInfo: true }), []);
  const headerColumns = useMemo(() => buildHeaderColumns(historical), [historical]);

  return (
    <div>
      <TransactionsFilters currentUser={currentUser} DYNAMICFORMSETTING={dynamicFormSetting} searchData={searchData} />
      <div style={{ height: "calc((100vh - 300px) / 2)" }}>
        <DataGrid
          dataSource={dataSource}
          columns={headerColumns}
          remoteOperations={true}
          showBorders={true}
          columnAutoWidth={true}
          height="100%"
          sorting={{ mode: "multiple" }}
          filterRow={{ visible: true }}
          headerFilter={{ visible: true }}
          columnChooser={{ enabled: true }}
          export={{ enabled: true }}
          selection={{ mode: "single" }}
          paging={{ pageSize: 20 }}
          pager={pager}
          onSelectionChanged={onSelectionChanged}
        />
      </div>
      <div style={{ marginTop: 8 }}>
        <DetailTabs ref={detailRef} accessToken={accessToken} />
      </div>
    </div>
  );
}

function mapStateToProps(state) { return { oidc: state.oidc }; }
export default connect(mapStateToProps)(Transactions);
